import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../shared/api.service';
import Swal from 'sweetalert2';

interface Rol {
    id: number;
    name: string;
}

interface Accion {
    id: number;
    name: string;
    is_active: boolean;
}

interface Modulo {
    id: number;
    name: string;
    is_active: boolean;
    actions: Accion[];
    children: Modulo[];
}

/** Un cambio local pendiente de guardar. Para acciones, module_id es el
 * módulo dueño de la acción (lo exige remove-action-permissions). */
interface CambioPendiente {
    tipo: 'modulo' | 'accion';
    module_id?: number;
    action_id?: number;
    is_active: boolean;
}

const NOMBRES_ACCIONES_ESTANDAR = ['Ver', 'Crear', 'Editar', 'Eliminar'];

@Component({
    selector: 'app-permission',
    templateUrl: './permission.component.html',
    styleUrls: ['./permission.component.css']
})
export class PermissionComponent implements OnInit {

    roles: Rol[] = [];
    selectedRoleId: number | null = null;
    modulos: Modulo[] = [];
    searchTerm = '';

    loadingRoles = false;
    loadingMatrix = false;

    expandedModuleId: number | null = null;
    bulkEditModuleId: number | null = null;
    bulkEditSelection: { [accionNombre: string]: boolean } = {};

    /** Cambios locales sin guardar, por clave "modulo_<id>" / "accion_<id>". */
    pendingChanges = new Map<string, CambioPendiente>();

    constructor(private apiService: ApiService) { }

    ngOnInit(): void {
        this.cargarRoles();
    }

    cargarRoles(): void {
        this.loadingRoles = true;
        this.apiService.get('user/roles').subscribe({
            next: (response: any) => {
                const data = response?.data ?? response ?? [];
                this.roles = data.map((rol: any) => ({ id: rol.id, name: rol.name }));
            },
            error: () => {
                Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar los roles' });
            },
            complete: () => {
                this.loadingRoles = false;
            }
        });
    }

    get hayCambiosPendientes(): boolean {
        return this.pendingChanges.size > 0;
    }

    get modulosFiltrados(): Modulo[] {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) return this.modulos;
        return this.modulos.filter(m =>
            m.name.toLowerCase().includes(term) ||
            m.children.some(c => c.name.toLowerCase().includes(term))
        );
    }

    get expandedModule(): Modulo | null {
        if (this.expandedModuleId === null) return null;
        return this.modulos.find(m => m.id === this.expandedModuleId) ?? null;
    }

    get resumen(): { modulosConSubmenu: number; submodulosTotales: number; conAcceso: number } {
        const modulosConSubmenu = this.modulos.filter(m => m.children.length > 0).length;
        const submodulosTotales = this.modulos.reduce((sum, m) => sum + m.children.length, 0);
        const conAcceso = this.modulos.filter(m => m.is_active).length
            + this.modulos.reduce((sum, m) => sum + m.children.filter(c => c.is_active).length, 0);

        return { modulosConSubmenu, submodulosTotales, conAcceso };
    }

    onRoleChange(): void {
        this.modulos = [];
        this.pendingChanges.clear();
        this.expandedModuleId = null;
        this.bulkEditModuleId = null;
        if (this.selectedRoleId === null) return;

        this.cargarMatriz();
    }

    private cargarMatriz(): void {
        this.loadingMatrix = true;
        this.apiService.get(`permissions/role/${this.selectedRoleId}/matrix`).subscribe({
            next: (response: any) => {
                this.modulos = (response?.data?.modules ?? []).map((m: any) => ({ ...m, children: m.children ?? [] }));
            },
            error: () => {
                Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo cargar la matriz de permisos' });
            },
            complete: () => {
                this.loadingMatrix = false;
            }
        });
    }

    toggleExpand(modulo: Modulo): void {
        this.expandedModuleId = this.expandedModuleId === modulo.id ? null : modulo.id;
        this.bulkEditModuleId = null;
    }

    accionesEstandarDe(modulo: Modulo): string[] {
        const nombres = new Set<string>();
        modulo.actions.forEach(a => nombres.add(a.name));
        modulo.children.forEach(c => c.actions.forEach(a => nombres.add(a.name)));
        // Prioriza el orden estándar; agrega cualquier acción no estándar al final.
        const ordenadas = NOMBRES_ACCIONES_ESTANDAR.filter(n => nombres.has(n));
        const extra = [...nombres].filter(n => !NOMBRES_ACCIONES_ESTANDAR.includes(n));
        return [...ordenadas, ...extra];
    }

    countActivePermissions(modulo: Modulo): number {
        const propias = modulo.actions.filter(a => a.is_active).length + (modulo.is_active ? 1 : 0);
        const deHijos = modulo.children.reduce((sum, c) =>
            sum + c.actions.filter(a => a.is_active).length + (c.is_active ? 1 : 0), 0);
        return propias + deHijos;
    }

    toggleModuleAccess(modulo: Modulo): void {
        const nuevoEstado = !modulo.is_active;
        modulo.is_active = nuevoEstado;
        this.pendingChanges.set(`modulo_${modulo.id}`, { tipo: 'modulo', module_id: modulo.id, is_active: nuevoEstado });

        // Al apagar un módulo, sus propias acciones y las de sus submódulos
        // se apagan también (no tiene sentido dejarlas activas sin acceso).
        if (!nuevoEstado) {
            modulo.actions.forEach(a => this.desactivarAccionLocal(a, modulo.id));
            modulo.children.forEach(child => {
                child.is_active = false;
                this.pendingChanges.set(`modulo_${child.id}`, { tipo: 'modulo', module_id: child.id, is_active: false });
                child.actions.forEach(a => this.desactivarAccionLocal(a, child.id));
            });
        }
    }

    private desactivarAccionLocal(accion: Accion, moduleId: number): void {
        if (!accion.is_active) return;
        accion.is_active = false;
        this.pendingChanges.set(`accion_${accion.id}`, { tipo: 'accion', action_id: accion.id, module_id: moduleId, is_active: false });
    }

    /** Activar cualquier acción da acceso automáticamente a su (sub)módulo
     * dueño (no tiene sentido una acción activa sin acceso al módulo) — pero
     * el acceso también se puede activar/desactivar explícitamente por su
     * propio switch (toggleChildAccess), independiente de si tiene o no
     * acciones activas en ese momento. */
    toggleAction(contenedor: Modulo, accion: Accion): void {
        if (!contenedor.is_active) return;

        accion.is_active = !accion.is_active;
        this.pendingChanges.set(`accion_${accion.id}`, { tipo: 'accion', action_id: accion.id, module_id: contenedor.id, is_active: accion.is_active });
    }

    /** Switch de "Acceso" de un submódulo (o de un módulo sin submenú),
     * independiente de sus acciones individuales. Al apagarlo, sus propias
     * acciones activas se apagan también (no tiene sentido dejarlas activas
     * sin acceso al submódulo). */
    toggleChildAccess(item: Modulo): void {
        const nuevoEstado = !item.is_active;
        item.is_active = nuevoEstado;
        this.pendingChanges.set(`modulo_${item.id}`, { tipo: 'modulo', module_id: item.id, is_active: nuevoEstado });

        if (!nuevoEstado) {
            item.actions.forEach(a => this.desactivarAccionLocal(a, item.id));
        }
    }

    esPendiente(tipo: 'modulo' | 'accion', id: number): boolean {
        return this.pendingChanges.has(`${tipo}_${id}`);
    }

    // --- Panel lateral: acciones rápidas ---

    abrirEdicionGrupo(modulo: Modulo): void {
        this.bulkEditModuleId = modulo.id;
        const seleccion: { [n: string]: boolean } = {};
        this.accionesEstandarDe(modulo).forEach(nombre => { seleccion[nombre] = false; });
        this.bulkEditSelection = seleccion;
    }

    cancelarEdicionGrupo(): void {
        this.bulkEditModuleId = null;
    }

    aplicarEdicionGrupo(modulo: Modulo): void {
        modulo.children.forEach(child => {
            child.is_active = true;
            this.pendingChanges.set(`modulo_${child.id}`, { tipo: 'modulo', module_id: child.id, is_active: true });

            child.actions.forEach(accion => {
                const activo = !!this.bulkEditSelection[accion.name];
                if (accion.is_active !== activo) {
                    accion.is_active = activo;
                    this.pendingChanges.set(`accion_${accion.id}`, { tipo: 'accion', action_id: accion.id, module_id: child.id, is_active: activo });
                }
            });
        });

        this.bulkEditModuleId = null;
    }

    quitarAccesoAlGrupo(modulo: Modulo): void {
        Swal.fire({
            title: '¿Quitar acceso al grupo?',
            text: `Se desactivará "${modulo.name}" y todos sus submódulos para este rol.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, quitar acceso',
            cancelButtonText: 'Cancelar'
        }).then(result => {
            if (result.isConfirmed && modulo.is_active) {
                this.toggleModuleAccess(modulo);
            }
        });
    }

    // --- Guardado por lote ---

    guardarCambios(): void {
        if (!this.hayCambiosPendientes || this.selectedRoleId === null) return;

        Swal.fire({
            title: '¿Guardar cambios?',
            text: `Se van a actualizar ${this.pendingChanges.size} permisos`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, guardar',
            cancelButtonText: 'Cancelar'
        }).then(result => {
            if (result.isConfirmed) {
                this.ejecutarGuardado();
            }
        });
    }

    private ejecutarGuardado(): void {
        const roleId = this.selectedRoleId;
        const cambios = [...this.pendingChanges.values()];

        const modulosActivar = cambios.filter(c => c.tipo === 'modulo' && c.is_active)
            .map(c => ({ role_id: roleId, module_id: c.module_id, is_active: true }));
        const modulosDesactivar = cambios.filter(c => c.tipo === 'modulo' && !c.is_active)
            .map(c => ({ role_id: roleId, module_id: c.module_id, is_active: false }));
        const accionesActivar = cambios.filter(c => c.tipo === 'accion' && c.is_active)
            .map(c => ({ role_id: roleId, action_id: c.action_id, is_active: true }));
        const accionesDesactivar = cambios.filter(c => c.tipo === 'accion' && !c.is_active)
            .map(c => ({ role_id: roleId, action_id: c.action_id, module_id: c.module_id }));

        const promises: Promise<any>[] = [];

        if (modulosActivar.length || modulosDesactivar.length) {
            promises.push(this.apiService.post('permissions/assign-module-permissions', {
                permissions: [...modulosActivar, ...modulosDesactivar]
            }).toPromise());
        }
        if (accionesActivar.length) {
            promises.push(this.apiService.post('permissions/assign-action-permissions', { permissions: accionesActivar }).toPromise());
        }
        if (accionesDesactivar.length) {
            promises.push(this.apiService.post('permissions/remove-action-permissions', { permissions: accionesDesactivar }).toPromise());
        }

        Promise.all(promises).then(() => {
            Swal.fire({ icon: 'success', title: '¡Guardado exitoso!', text: 'Los permisos han sido actualizados correctamente' });
            this.pendingChanges.clear();
            this.cargarMatriz();
        }).catch(() => {
            Swal.fire({ icon: 'error', title: 'Error al guardar', text: 'No se pudieron guardar los cambios. Inténtalo nuevamente.' });
        });
    }
}
